import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtGHS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ProductCombobox } from "@/components/ProductCombobox";
import { Plus, Trash2, Pill } from "lucide-react";
import { toast } from "sonner";

const line = () => ({ product_id: "", quantity: 1, covered: true, ins: "" });

// Add one or more medicines to an admitted patient's tab; stock is taken FEFO immediately.
export function DispensePanel({ a, products, onDone }) {
  const [lines, setLines] = useState([line()]);
  const [busy, setBusy] = useState(false);
  const [stock, setStock] = useState(null);
  const loadStock = useCallback(() => { api.get(`/inpatients/${a.id}/stock`).then((r) => setStock(r.data)).catch(() => setStock(null)); }, [a.id]);
  useEffect(() => { loadStock(); }, [loadStock]);
  const needed = (pid) => lines.filter((l) => l.product_id === pid).reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const short = (l) => stock && l.product_id && needed(l.product_id) > (stock[l.product_id] || 0);
  const anyShort = lines.some(short);
  const insured = a.payer_type === "insurance";
  const set = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const valid = lines.filter((l) => l.product_id && Number(l.quantity) > 0);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inpatients/${a.id}/dispense`, { lines: valid.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity),
        covered: insured && l.covered, insurance_amount: insured && l.covered && l.ins !== "" ? Number(l.ins) : null })) });
      toast.success(`${valid.length} medicine(s) dispensed to ${a.patient_name}`);
      setLines([line()]); onDone(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); loadStock(); }
  };
  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="ip-dispense-panel">
      <div className="text-sm font-medium flex items-center gap-1.5"><Pill className="h-4 w-4" /> Dispense</div>
      {lines.map((l, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[240px]"><ProductCombobox products={products} value={l.product_id} onChange={(v) => set(i, { product_id: v })} testId={`ip-disp-product-${i}`} stock={stock} /></div>
          <Input type="number" min={1} className="w-20 h-9" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} data-testid={`ip-disp-qty-${i}`} />
          {insured && <label className="flex items-center gap-1 text-xs"><Checkbox checked={l.covered} onCheckedChange={(v) => set(i, { covered: !!v })} data-testid={`ip-disp-covered-${i}`} /> covered</label>}
          {insured && l.covered && <label className="flex items-center gap-1 text-xs text-sky-700">Ins. pays
            <Input type="number" min={0} step="0.01" placeholder="full" value={l.ins} onChange={(e) => set(i, { ins: e.target.value })} className="h-8 w-24" data-testid={`ip-disp-ins-${i}`} /></label>}
          {stock && l.product_id && <span className={`text-xs ${short(l) ? "text-rose-600 font-medium" : "text-muted-foreground"}`} data-testid={`ip-disp-avail-${i}`}>
            {short(l) ? `Only ${stock[l.product_id] || 0} in stock` : `${stock[l.product_id]} available`}</span>}
          <button className="text-muted-foreground hover:text-rose-600" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} data-testid={`ip-disp-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={() => setLines((ls) => [...ls, line()])} data-testid="ip-disp-add-line"><Plus className="h-3.5 w-3.5 mr-1" /> Add line</Button>
        <Button size="sm" disabled={busy || !valid.length || anyShort} onClick={save} data-testid="ip-disp-save">{busy ? "Saving…" : "Dispense & take from stock"}</Button>
        {anyShort && <span className="text-xs text-rose-600 self-center" data-testid="ip-disp-short-warning">Not enough stock at this branch — reduce the quantity or remove the line.</span>}
        {insured && <span className="text-xs text-muted-foreground self-center">Blank "Ins. pays" = fully covered; the patient owes any top-up.</span>}
      </div>
    </div>
  );
}
